import { useNavigate } from "react-router-dom";
import useAppState from "../../context/AppContext";
import { APP_RETIREMENT_URLS, ERROR_MESSAGES } from "../../const/AppConst";
import StickButtonsBloc from "../common/StickButtonsBloc";
import SituationInfoFormComponent from "../../components/retirement/situation/SituationInfoComponent";
import { useForm } from "react-hook-form";
import SituationBeneficiaryFormComponent from "../../components/retirement/situation/SituationBeneficiaryFormComponent";
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { getAge, getStepsWithBeneficiaryInfo, isValidDate } from "../../utils/utils";
import { IAdditionalData, IChildDetail, IPartnerDetail } from "../../context/model";
import moment from "moment";

type SituationBlocFormData = {
  birthDate: string;
  retirementDate: string;
  alsaceMoselleRegime: string;
  hasChild: string;
  hasPartner: string;
  childrenList: IChildDetail[];
  partnerInfo: IPartnerDetail
}


export default function RESituationBloc() {
  const navigate = useNavigate();
  const {
    state: { updatedProposition, additionalData, infoSteps, proposition },
    dispatch
  } = useAppState();

  const validationSchema = Yup.object().shape({
    birthDate: Yup.string()
      .required(ERROR_MESSAGES.requiredMsg)
      .test("validDate", ERROR_MESSAGES.invalidMsg, (val) => {
        let now = new Date();
        now.setHours(0,0,0,0);
        return val !== undefined && isValidDate(val) && new Date(val) < now;
      }),
    retirementDate: Yup.string()
      .required(ERROR_MESSAGES.requiredMsg)
      .test("after1950","L'année renseignée est incorrecte.", (val) => {
        if(!val) return false;
        const rightValType = new Date(val)?.toISOString()?.slice(0,10);
        const minimumDate = new Date('1950-01-01');
        minimumDate.setHours(0,0,0,0);
        return val !== undefined  && new Date(rightValType)> minimumDate;
      })
      .test("validDate", ERROR_MESSAGES.invalidMsg, (val) => {
        if(!val) return false;
        const rightValType = new Date(val)?.toISOString()?.slice(0,10);
        let now = new Date();
        now.setHours(0,0,0,0);
        return val !== undefined && isValidDate(rightValType);
      })
      .when('birthDate', (birthDate, schema) => {
          if(birthDate && moment(birthDate).isValid()){  
            // retirement day must after birthday  
            let now = new Date();
            now.setHours(0,0,0,0);    
            return schema.test(
              "follow-validRetirementDate", 
              "La date à la retraite doit être ultérieure à la date de naissance.", 
              (val:string) => { 
                if(!val) return val;     
                const rightValType = new Date(val)?.toISOString()?.slice(0,10);
                return (rightValType !== undefined && 
                isValidDate(rightValType) &&
                new Date(rightValType) > new Date(birthDate)  
                )
              });     
          }        
          return schema;
      }),
      
    alsaceMoselleRegime: Yup.string()
    .required(ERROR_MESSAGES.requiredMsg),
    hasChild: Yup.string()
    .required(ERROR_MESSAGES.requiredMsg),
    hasPartner: Yup.string()
    .required(ERROR_MESSAGES.requiredMsg),
    // .oneOf(['true', 'false'], ERROR_MESSAGES.invalidMsg),
    partnerInfo: Yup.object().when('hasPartner', {
      is: "true",
      then: Yup.object().shape({
        birthDate: Yup.string()
          .required(ERROR_MESSAGES.requiredMsg)
          .test("validDate", ERROR_MESSAGES.invalidMsg, (val) => {
            let now = new Date();
            now.setHours(0, 0, 0, 0);
            return val !== undefined && isValidDate(val) && new Date(val) < now;
          }),
        hasAlsaceMoselle: Yup.boolean(),
      })
    }),
    childrenList: Yup.array().when('hasChild', {
      is: "true",
      then: Yup.array().of(
        Yup.object().shape({
          birthDate: Yup.string()
          .required(ERROR_MESSAGES.requiredMsg)
          .test("validDate", ERROR_MESSAGES.invalidMsg, (val) => {
            let now = new Date();
            now.setHours(0, 0, 0, 0);
            return val !== undefined && isValidDate(val) && new Date(val) < now;
          })
          .when('hasHandicap', (hasHandicap, schema) => {
            if(!hasHandicap){  
              return schema.test("age-limit", ERROR_MESSAGES.invalidAge, (val:string) => {
                      let age
                      if(val){
                        age=getAge((new Date(val)).toString())
                      }
                      return val!==undefined && age!==undefined && age<25;
                    })
              
            }        
            return schema;
          })
          ,
        })
      )
        
    }),
    numberOfChild: Yup.string().when('hasChild', (val, chema)=> {
      if(val === true){
        return Yup.number()
                  .min(1, "Nombre d'enfants devrait être supérieur à 1.")
                  .max(5, "Nombre d'enfants devrait être inférieur à 5.")
      } 
      return Yup.string().notRequired()
      
    })

  })
  const {
    register,
    handleSubmit,
    getValues,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<SituationBlocFormData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      birthDate: updatedProposition?.birthDate  ?? "", 
      retirementDate: updatedProposition?.retirementDate ?? "",
      alsaceMoselleRegime: 
        typeof updatedProposition?.alsaceMoselleRegime === 'boolean' 
        ? String(updatedProposition?.alsaceMoselleRegime) 
        : '',
      hasChild: 
        typeof additionalData?.hasChild === 'boolean' 
        ? String(additionalData?.hasChild) 
        : '',
      hasPartner: 
        typeof additionalData?.hasPartner === 'boolean' 
        ? String(additionalData?.hasPartner) 
        : '',
      childrenList: additionalData?.children ?? [],
      partnerInfo: 
        additionalData?.partnerInfo 
        ?? {birthDate: '', hasAlsaceMoselle: false}
    },
    resolver: yupResolver(validationSchema)
  });

  const onNextAction = async (data: SituationBlocFormData) => {
    let tempUpdatedPropo = updatedProposition;
    let tempAdditional = additionalData ?? {} as IAdditionalData;


      if(tempUpdatedPropo) {
        tempUpdatedPropo.birthDate = data.birthDate;
        tempUpdatedPropo.retirementDate =  data.retirementDate;
        tempUpdatedPropo.alsaceMoselleRegime = data.alsaceMoselleRegime === 'true';

        tempAdditional.hasChild = data.hasChild.toString() === 'true';
        tempAdditional.hasPartner = data.hasPartner.toString() === 'true';
        tempAdditional.children = data.childrenList;
        tempAdditional.partnerInfo = data.partnerInfo
      }

      const hasBeneficiaryStep = getStepsWithBeneficiaryInfo(
        infoSteps, 
        data.hasChild.toString() === 'true' || data.hasPartner.toString() === 'true');
  
      if (hasBeneficiaryStep.hasChanged) {
        dispatch({type: "UPDATE_STEPS", steps: hasBeneficiaryStep.infoSteps})
      }
  
      const nextUrl = APP_RETIREMENT_URLS.besoinPage;
      dispatch({
        type: 'NEXT_INFO_STEP', 
        path: nextUrl, 
        updatedProposition: tempUpdatedPropo, 
        additionalData: tempAdditional
      });
      navigate({
          pathname: nextUrl,
          search: `?id=${proposition!.propositionId}`,
      });  
      
  }

  const onPreviousAction = async () => {
    const url = APP_RETIREMENT_URLS.bienvenuePage
    dispatch({type: 'BACK_INFO_STEP', path: url});
    navigate({
        pathname: url,
        search: `?id=${proposition!.propositionId}`,
    });         
  }

  return (
    <form style={{width: '100%'}}>
        <div className='form-row page-content'>
          <div className='col-flex-4 bg-blanc shadow-noir p-lg m-y-xs'>
            <SituationInfoFormComponent 
              register={register} 
              errors={errors} 
              setValue={setValue}
              watch={watch}
              />
          </div>
          
          <div className='col-flex-4 bg-blanc shadow-noir p-lg m-y-xs'>
            <SituationBeneficiaryFormComponent 
              register={register} errors={errors} 
              getValues={getValues} watch={watch} 
              setValue={setValue} 
              childrenList={getValues('childrenList')} 
              partnerInfo={getValues('partnerInfo')}
              clearErrors={clearErrors}
            />
          </div>
        </div>
        <StickButtonsBloc 
            showPreviousButton={true} 
            disabledPreviousButton={false} 
            nextAction={handleSubmit(onNextAction)} 
            showNextButton={true} 
            previousAction={onPreviousAction}
        />
    </form>
  )
}